import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, Plus, Printer, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { viewFor } from "@/config/roleViews";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Field, Pill, RollPicker, adjustedRequired, money, qty, today, unit } from "@/components/stores/storesUi";
import { SheetBody } from "@/components/stores/StoresSheet";
import { printElement } from "@/lib/print";

const SAVE_BTN = "bg-[#FF5A00] hover:bg-[#e04f00]";

function useStoreLists() {
  const [lists, setLists] = useState({ items: [], suppliers: [], categories: [], departments: [] });
  const load = () =>
    Promise.all([api.get("/stores/items"), api.get("/stores/suppliers"), api.get("/options")])
      .then(([items, suppliers, options]) =>
        setLists({
          items: items.data,
          suppliers: suppliers.data.map((s) => s.supplier_name),
          categories: options.data.store_category || [],
          departments: options.data.store_department || [],
        }))
      .catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return { ...lists, reload: load };
}

/** "AMA2030-200" -> paper code AMA2030, width 200 (item codes are paper code + "-" + width). */
function splitItemCode(code) {
  const m = /^(.+)-(\d+(?:\.\d+)?)$/.exec((code || "").trim());
  return m ? { paper_code: m[1], width: m[2] } : { paper_code: (code || "").trim(), width: "" };
}

function Labeled({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="label-caps block mb-1 text-[10px]">{label}</label>
      {children}
    </div>
  );
}

function SmallSelect({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="rounded-sm h-8 text-sm"><SelectValue /></SelectTrigger>
      <SelectContent className="max-h-72">
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

async function submit(setBusy, call, done) {
  setBusy(true);
  try {
    const result = await call();
    done(result);
  } catch (e) {
    toast.error(formatApiError(e));
  } finally {
    setBusy(false);
  }
}

// ----------------------------------------------------------------------------
// Item (stores item master entry)
// ----------------------------------------------------------------------------
// `preset` fills a new item from where it was started (a goods receipt line: code, supplier, rate).
export function ItemDialog({ itemCode, preset, onClose, onSaved }) {
  const { user } = useAuth();
  const showMoney = viewFor(user).finance;
  const lists = useStoreLists();
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    paper_code: "", width: "", material_name: "", category: "", supplier: "", gsm: "",
    uom: "SQMTRS", rate: "", min_stock: "", max_stock: "", rack: "", status: "Active",
    ...(itemCode ? {} : preset),
  });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!itemCode) return;
    api.get("/stores/items", { params: { q: itemCode } }).then(({ data }) => {
      const it = data.find((x) => x.sku === itemCode);
      if (!it) return;
      setF({
        paper_code: it.paper_code || "", width: it.roll_width ?? "", material_name: it.paper_type || "",
        category: it.type || "", supplier: it.supplier || "", gsm: it.gsm ?? "", uom: it.uom || "SQMTRS",
        rate: it.rate ?? "", min_stock: it.min_stock_level ?? "", max_stock: it.max_stock_level ?? "",
        rack: it.rack || "", status: it.status || "Active",
      });
    });
  }, [itemCode]);

  const code = f.paper_code && f.width ? `${f.paper_code.trim().toUpperCase()}-${Number(f.width)}` : "";
  const save = () => {
    const num = (v) => (v === "" || v == null ? null : Number(v));
    const body = {
      paper_code: f.paper_code.trim(), width: Number(f.width), material_name: f.material_name.trim(),
      category: f.category || null, supplier: f.supplier || null, gsm: num(f.gsm), uom: f.uom,
      min_stock: num(f.min_stock) || 0, max_stock: num(f.max_stock) || 0, rack: f.rack || null, status: f.status,
      rate: showMoney ? num(f.rate) : null,
    };
    submit(setBusy,
      () => (itemCode ? api.patch(`/stores/items/${encodeURIComponent(itemCode)}`, body) : api.post("/stores/items", body)),
      () => { toast.success(itemCode ? "Item updated" : `Item ${code} created`); onSaved(itemCode || code); });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{itemCode ? `Edit item · ${itemCode}` : "New item"}</DialogTitle>
          <DialogDescription>Item code is the paper code and width, e.g. AMA2030-200.</DialogDescription>
        </DialogHeader>
        <datalist id="store-categories">{lists.categories.map((c) => <option key={c} value={c} />)}</datalist>
        <datalist id="store-suppliers">{lists.suppliers.map((s) => <option key={s} value={s} />)}</datalist>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Paper code *" value={f.paper_code} onChange={set("paper_code")} disabled={!!itemCode} testid="item-paper-code" />
          <Field label="Width (mm) *" type="number" value={f.width} onChange={set("width")} disabled={!!itemCode} testid="item-width" />
          <Labeled label="Item code"><div className="h-8 flex items-center font-mono-num text-sm text-neutral-600">{code || "—"}</div></Labeled>
          <Field label="Material name *" value={f.material_name} onChange={set("material_name")} className="col-span-2" testid="item-name" />
          <Labeled label="UOM">
            <SmallSelect value={f.uom} onChange={set("uom")} options={[{ value: "SQMTRS", label: "SQM (stocked in metres)" }, { value: "KG", label: "KG" }]} />
          </Labeled>
          <Field label="Category" value={f.category} onChange={set("category")} list="store-categories" className="col-span-2" />
          <Field label="GSM" type="number" value={f.gsm} onChange={set("gsm")} />
          <Field label="Supplier" value={f.supplier} onChange={set("supplier")} list="store-suppliers" className="col-span-2" />
          <Field label="Rack" value={f.rack} onChange={set("rack")} />
          <Field label="Min stock" type="number" value={f.min_stock} onChange={set("min_stock")} />
          <Field label="Max stock" type="number" value={f.max_stock} onChange={set("max_stock")} />
          {showMoney && <Field label="Rate (per SQM / kg)" type="number" value={f.rate} onChange={set("rate")} />}
          <Labeled label="Status">
            <SmallSelect value={f.status} onChange={set("status")} options={[{ value: "Active", label: "Active" }, { value: "Inactive", label: "Inactive" }]} />
          </Labeled>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="item-save" className={SAVE_BTN} onClick={save}
            disabled={busy || !f.paper_code.trim() || !Number(f.width) || !f.material_name.trim()}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------------
// Goods receipt (GRN): one line per roll
// ----------------------------------------------------------------------------
// Item codes are "<prefix>-<width>": the prefix is the paper code for most items, a material name for a few
// imported ones (GOLD-340). Some imported codes carry a stray line break inside the prefix, so prefixes are
// compared folded to single spaces, and the exact stored code is what goes to the server.
const SKU_RX = /^([\s\S]+)-(\d+(?:\.\d+)?)$/;
const foldCode = (s) => String(s ?? "").replace(/\s+/g, " ").trim().toUpperCase();
const widthKey = (w) => {
  const s = String(w ?? "").trim();
  return s !== "" && Number.isFinite(Number(s)) ? String(Number(s)) : null;
};
function skuParts(sku) {
  const m = SKU_RX.exec(String(sku ?? "").trim());
  return m ? { prefix: foldCode(m[1]), width: widthKey(m[2]) } : null;
}

// prefix / width are what was typed; item_code is the stored code they resolve to ("" until both are in).
// type is only typed when the item master has none. gsm pre-fills from the master; gsmTouched marks a value
// typed over it — a blank master gets it filled, a different value deliberately changes the master.
const blankLine = () => ({
  prefix: "", width: "", item_code: "", roll_no: "", master_lot: "", received_qty: "", rate: "", type: "", gsm: "", gsmTouched: false,
});

// Qty received is in the invoice's unit: SQM for paper / film (stocked in running metres,
// metres = SQM ÷ width in m), kg for KG items. Amount = Qty received × Rate, as on the
// invoice and in GOODS RECEIPT (backend/routes/stores.py create_receipt).
export function ReceiptDialog({ onClose, onDone }) {
  const lists = useStoreLists();
  const items = useMemo(() => Object.fromEntries(lists.items.map((i) => [i.sku, i])), [lists.items]);
  // Each family of widths (one prefix): the stored code per width, and — when the widths are one material,
  // i.e. one type across them — the type / GSM they agree on, which a width still blank in the master borrows.
  // A catch-all code mixing materials (the imported "NA" items) never forms one family: they differ in prefix.
  const families = useMemo(() => {
    const fams = {};
    for (const i of lists.items) {
      const p = skuParts(i.sku);
      if (!p?.width) continue;
      const f = fams[p.prefix] || (fams[p.prefix] = { codes: {}, types: new Map(), gsms: new Set() });
      f.codes[p.width] = i.sku;
      if (String(i.type ?? "").trim()) f.types.set(String(i.type).trim().toLowerCase(), String(i.type).trim());
      if (Number(i.gsm) > 0) f.gsms.add(Number(i.gsm));
    }
    for (const f of Object.values(fams)) {
      f.widths = Object.keys(f.codes).sort((a, b) => Number(a) - Number(b));
      const one = f.types.size <= 1;
      f.type = one && f.types.size === 1 ? [...f.types.values()][0] : null;
      f.gsm = one && f.gsms.size === 1 ? [...f.gsms][0] : null;
    }
    return fams;
  }, [lists.items]);
  const familyOf = (it) => {
    const p = it && skuParts(it.sku);
    return p ? families[p.prefix] : null;
  };
  // Types already in the master, one per spelling regardless of case, as suggestions for a blank one.
  const typeOptions = useMemo(() => {
    const seen = new Map();
    for (const i of lists.items) {
      const t = String(i.type ?? "").trim();
      if (t && !seen.has(t.toLowerCase())) seen.set(t.toLowerCase(), t);
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [lists.items]);
  // Type / GSM as the master has them: the item's own, else what its sibling widths agree on.
  const masterType = (it) => (it ? String(it.type ?? "").trim() || familyOf(it)?.type || null : null);
  const masterGsm = (it) => (it ? (Number(it.gsm) > 0 ? Number(it.gsm) : familyOf(it)?.gsm || null) : null);

  const [busy, setBusy] = useState(false);
  const [head, setHead] = useState({ date: today(), supplier: "", invoice_no: "", invoice_date: "" });
  const [lines, setLines] = useState([blankLine()]);
  // A roll of an item not in the item list yet: create it here, then the line carries on.
  const [newItem, setNewItem] = useState(null); // { line, preset }

  const setLine = (i, k, v) => setLines((ls) => ls.map((ln, j) => (j === i ? { ...ln, [k]: v } : ln)));
  // The typed code + width resolve to the exact stored item code. A width the newly typed code doesn't come
  // in is cleared, rather than left showing as though it matched.
  const setParts = (i, prefix, width) => setLines((ls) => ls.map((ln, j) => {
    if (j !== i) return ln;
    const code = foldCode(prefix);
    const fam = families[code];
    const w = fam && widthKey(width) && !fam.codes[widthKey(width)] ? "" : width;
    const item_code = !code || !widthKey(w) ? "" : fam?.codes[widthKey(w)] || `${code}-${widthKey(w)}`;
    // A GSM typed for one material doesn't carry over to another; another width of the same one keeps it.
    const gsmReset = code !== foldCode(ln.prefix) ? { gsm: "", gsmTouched: false } : {};
    return { ...ln, prefix, width: w, item_code, ...gsmReset };
  }));
  const startNewItem = (line) => {
    const ln = lines[line] || blankLine();
    const parts = ln.prefix ? { paper_code: foldCode(ln.prefix), width: widthKey(ln.width) || "" } : splitItemCode(ln.item_code);
    setNewItem({ line, preset: { ...parts, supplier: head.supplier.trim(), ...(ln.rate !== "" && { rate: ln.rate }) } });
  };
  const itemCreated = async (code) => {
    const { line } = newItem;
    setNewItem(null);
    await lists.reload();
    const p = skuParts(code);
    const fill = { prefix: p ? p.prefix : code, width: p?.width || "", item_code: code };
    // The new item goes on the line it was started from, or the first empty line.
    setLines((ls) => {
      const at = line != null ? line : ls.findIndex((ln) => !ln.item_code && !ln.prefix);
      if (at < 0) return [...ls, { ...blankLine(), ...fill }];
      return ls.map((ln, j) => (j === at ? { ...ln, ...fill } : ln));
    });
  };
  const metres = (ln) => {
    const it = items[ln.item_code];
    const q = Number(ln.received_qty);
    if (!it || !q) return null;
    return it.uom === "SQMTRS" ? q / (it.roll_width / 1000) : q;
  };
  // GSM typed over the pre-fill (null when left as the master has it, or cleared).
  const typedGsm = (ln) => (ln.gsmTouched && String(ln.gsm).trim() !== "" ? Number(ln.gsm) : null);
  // GSM in force for a line: what was typed over the pre-fill, else the master's (own or its other widths').
  const gsmOf = (ln) => {
    const g = ln.gsmTouched ? typedGsm(ln) : masterGsm(items[ln.item_code]);
    return g > 0 && g <= 2000 ? g : null;
  };
  // What saving does to the master's GSM: fill a blank, or deliberately change an existing value.
  const gsmEffect = (ln) => {
    const typed = typedGsm(ln), master = masterGsm(items[ln.item_code]);
    if (typed == null || !(typed > 0 && typed <= 2000)) return null;
    if (master == null) return { kind: "fill", value: typed };
    return typed !== master ? { kind: "change", value: typed, from: master } : null;
  };
  // kg = GSM × width (m) × length (m) ÷ 1000 — the same as SQM × GSM ÷ 1000, written the way the floor
  // works it out. A kg item runs it backwards: length = kg × 1000 ÷ (GSM × width).
  const weight = (ln) => {
    const it = items[ln.item_code];
    const q = Number(ln.received_qty), g = gsmOf(ln), widthM = Number(it?.roll_width) / 1000;
    if (!it || !(q > 0) || !(widthM > 0)) return null;
    if (it.uom === "KG") return { kg: q, gsm: g, widthM, lengthM: g ? (q * 1000) / (g * widthM) : null };
    const lengthM = q / widthM;
    return { kg: g ? (g * widthM * lengthM) / 1000 : null, gsm: g, widthM, lengthM };
  };
  // Rate typed from the invoice; left blank, the item's own rate is used on save.
  const rateOf = (ln) => (ln.rate !== "" ? Number(ln.rate) : items[ln.item_code]?.rate ?? null);
  const amountOf = (ln) => {
    const q = Number(ln.received_qty), r = rateOf(ln);
    return q > 0 && r != null && !Number.isNaN(r) ? q * r : null;
  };
  const filled = lines.filter((ln) => ln.prefix || ln.item_code || ln.roll_no || ln.received_qty);
  const badGsm = (ln) => typedGsm(ln) != null && !(typedGsm(ln) > 0 && typedGsm(ln) <= 2000);
  const invalid = filled.some((ln) => !items[ln.item_code] || !ln.roll_no.trim() || !(Number(ln.received_qty) > 0)
    || (ln.rate !== "" && !(Number(ln.rate) >= 0)) || badGsm(ln));
  const totals = filled.reduce((t, ln) => {
    const it = items[ln.item_code];
    const q = Number(ln.received_qty) || 0;
    if (it?.uom === "KG") t.kg += q;
    else if (it) { t.sqm += q; t.m += metres(ln) || 0; }
    const w = weight(ln);
    if (w?.kg != null) t.weight += w.kg;
    else if (w) t.noGsm += 1;
    const a = amountOf(ln);
    if (a != null) t.amount += a;
    else if (q) t.unpriced += 1;
    return t;
  }, { sqm: 0, m: 0, kg: 0, weight: 0, noGsm: 0, amount: 0, unpriced: 0 });

  const save = () => submit(setBusy, () => api.post("/stores/receipts", {
    date: head.date,
    supplier: head.supplier.trim(),
    invoice_no: head.invoice_no || null,
    invoice_date: head.invoice_date || null,
    lines: filled.map((ln) => {
      const it = items[ln.item_code];
      // Type only ever fills the item's own blank (from its other widths, else what was typed). GSM does the
      // same, except a value deliberately typed over the master's is sent flagged as a change to the master.
      const ownType = String(it?.type ?? "").trim(), ownGsm = Number(it?.gsm) > 0;
      const gsm = gsmEffect(ln);
      return {
        item_code: ln.item_code,
        roll_no: ln.roll_no.trim(),
        master_lot: ln.master_lot.trim() || null,
        received_qty: Number(ln.received_qty),
        rate: ln.rate !== "" ? Number(ln.rate) : null,
        type: ownType ? null : masterType(it) || (ln.type || "").trim() || null,
        gsm: gsm ? gsm.value : (!ownGsm && masterGsm(it)) || null,
        gsm_update: gsm?.kind === "change",
      };
    }),
  }), ({ data }) => {
    const converted = totals.sqm > 0 ? ` · ${qty(totals.sqm)} SQM = ${qty(totals.m)} m` : "";
    const kg = totals.kg > 0 ? ` · ${qty(totals.kg)} kg` : "";
    toast.success(`${data.grn_no} · ${data.lines} roll(s) received${converted}${kg}, QC pending`);
    const label = { gsm: "GSM filled", gsm_changed: "GSM changed", type: "type filled" };
    const filledIn = Object.entries(data.master_filled || {}).filter(([, n]) => n > 0)
      .map(([f, n]) => `${label[f] || f} on ${n} item width(s)`);
    if (filledIn.length) toast.success(`Item master updated: ${filledIn.join(", ")}`);
    onDone();
  });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Goods receipt</DialogTitle>
          <DialogDescription>
            One line per roll, quantity and rate as on the invoice. Metres and amount are worked out as you type.
            Rolls stay on QC hold until approved; the GRN number is assigned on save.
          </DialogDescription>
        </DialogHeader>
        <datalist id="grn-suppliers">{lists.suppliers.map((s) => <option key={s} value={s} />)}</datalist>
        <datalist id="grn-paper-codes">{Object.keys(families).sort().map((c) => <option key={c} value={c} />)}</datalist>
        <datalist id="grn-types">{typeOptions.map((t) => <option key={t} value={t} />)}</datalist>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="GRN date" type="date" value={head.date} onChange={(v) => setHead({ ...head, date: v })} />
          <Field label="Supplier *" value={head.supplier} onChange={(v) => setHead({ ...head, supplier: v })} list="grn-suppliers" testid="grn-supplier" />
          <Field label="Invoice no." value={head.invoice_no} onChange={(v) => setHead({ ...head, invoice_no: v })} />
          <Field label="Invoice date" type="date" value={head.invoice_date} onChange={(v) => setHead({ ...head, invoice_date: v })} />
        </div>
        <div className="border border-neutral-200 mt-1 overflow-x-auto">
          <table className="w-full text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap">
            <thead className="bg-neutral-50 label-caps text-[10px]">
              <tr>
                <th className="text-left px-2 py-2 w-56">Item code<div className="normal-case tracking-normal font-normal text-neutral-400">paper code · width</div></th>
                <th className="text-left px-2 py-2">Type</th>
                <th className="text-right px-2 py-2">GSM</th>
                <th className="text-left px-2 py-2">Roll no. *</th>
                <th className="text-left px-2 py-2">Master lot</th>
                <th className="text-right px-2 py-2">Qty received<div className="normal-case tracking-normal font-normal text-neutral-400">SQM / kg</div></th>
                <th className="text-right px-2 py-2">Metres<div className="normal-case tracking-normal font-normal text-neutral-400">stock qty</div></th>
                <th className="text-right px-2 py-2">KG<div className="normal-case tracking-normal font-normal text-neutral-400">GSM × W × L ÷ 1000</div></th>
                <th className="text-right px-2 py-2">Rate<div className="normal-case tracking-normal font-normal text-neutral-400">₹ per SQM / kg</div></th>
                <th className="text-right px-2 py-2">Amount<div className="normal-case tracking-normal font-normal text-neutral-400">qty × rate</div></th>
                <th className="w-16"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((ln, i) => {
                const it = items[ln.item_code];
                const m = metres(ln);
                const amount = amountOf(ln);
                return (
                  <tr key={i} className="border-t border-neutral-100 align-top">
                    <td className="px-2 py-1.5">
                      {(() => {
                        const fam = families[foldCode(ln.prefix)];
                        return (
                          <div className="flex gap-1">
                            {/* Not trimmed while typing: codes like "WHITE PET" have spaces in them. */}
                            <Input list="grn-paper-codes" value={ln.prefix} placeholder="AMA2030"
                              onChange={(e) => setParts(i, e.target.value.toUpperCase(), ln.width)}
                              className="rounded-sm h-8 text-sm font-mono-num w-28" data-testid={`grn-item-${i}`} />
                            {fam ? (
                              <select value={widthKey(ln.width) || ""} onChange={(e) => setParts(i, ln.prefix, e.target.value)}
                                className="rounded-sm h-8 text-sm font-mono-num border border-neutral-200 bg-white w-20 px-1" data-testid={`grn-width-${i}`}>
                                <option value="">width</option>
                                {fam.widths.map((w) => <option key={w} value={w}>{w}</option>)}
                              </select>
                            ) : (
                              <Input value={ln.width} placeholder="width" inputMode="decimal" onChange={(e) => setParts(i, ln.prefix, e.target.value)}
                                className="rounded-sm h-8 text-sm font-mono-num w-20" data-testid={`grn-width-${i}`} />
                            )}
                          </div>
                        );
                      })()}
                      <div className="text-[11px] text-neutral-500 mt-0.5 max-w-[14rem]" data-testid={`grn-item-info-${i}`}>
                        {/* Re-read from the item master whenever the code or width changes, so another width
                            (another item) always shows its own details, never the previous one's. */}
                        {it && <div className="truncate" title={it.paper_type}>{it.paper_type} · {it.uom === "KG" ? "KG" : "SQM"}</div>}
                        {!it && families[foldCode(ln.prefix)] && !widthKey(ln.width) && <span>choose the width</span>}
                        {!it && foldCode(ln.prefix) && (!families[foldCode(ln.prefix)] || widthKey(ln.width)) && (
                          <>
                            <span className="text-rose-600">Not in item list</span>
                            {" · "}
                            <button type="button" className="text-[#FF5A00] hover:underline font-medium" onClick={() => startNewItem(i)}
                              data-testid={`grn-new-item-${i}`}>
                              Add as new item
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                    {/* Type and GSM fill from the item master; where the master is blank they open up, and
                        what's typed is written into the master on save so the next receipt fills itself. */}
                    <td className="px-2 py-1.5" data-testid={`grn-type-cell-${i}`}>
                      {(() => {
                        if (!it) return <span className="text-neutral-300 text-sm leading-8">—</span>;
                        const t = masterType(it);
                        if (t) return (
                          <div className="max-w-[11rem]">
                            <div className="text-sm leading-8 truncate" title={t}>{t}</div>
                            {!String(it.type ?? "").trim() && <div className="text-[10px] text-neutral-400 -mt-1.5">from its other widths</div>}
                          </div>
                        );
                        return <Input list="grn-types" value={ln.type || ""} onChange={(e) => setLine(i, "type", e.target.value)} placeholder="not in master"
                          className="rounded-sm h-8 text-sm w-44 border-amber-300 bg-amber-50/40" data-testid={`grn-type-${i}`} />;
                      })()}
                    </td>
                    <td className="px-2 py-1.5 text-right" data-testid={`grn-gsm-cell-${i}`}>
                      {(() => {
                        if (!it) return <span className="text-neutral-300 text-sm leading-8">—</span>;
                        // Pre-filled from the master and still editable: a different value is a deliberate
                        // change to the master, and says so before anything is saved.
                        const master = masterGsm(it), effect = gsmEffect(ln);
                        const tone = badGsm(ln) ? "border-rose-400 bg-rose-50/40"
                          : effect?.kind === "change" || master == null ? "border-amber-300 bg-amber-50/40" : "";
                        return (
                          <div>
                            <Input type="number" min="1" max="2000" step="any" inputMode="decimal"
                              value={ln.gsmTouched ? ln.gsm : master ?? ""} placeholder={master != null ? String(master) : "GSM"}
                              onChange={(e) => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, gsm: e.target.value, gsmTouched: true } : x)))}
                              className={`rounded-sm h-8 text-sm text-right w-20 ml-auto ${tone}`} data-testid={`grn-gsm-${i}`} />
                            <div className="text-[10px] mt-0.5 whitespace-nowrap" data-testid={`grn-gsm-note-${i}`}>
                              {effect?.kind === "change" && <span className="text-amber-700 font-semibold">updates master {effect.from} → {effect.value}</span>}
                              {effect?.kind === "fill" && <span className="text-neutral-500">saved to master</span>}
                              {!effect && !ln.gsmTouched && master != null && !(Number(it.gsm) > 0) && <span className="text-neutral-400">from its other widths</span>}
                              {!effect && master == null && !typedGsm(ln) && <span className="text-amber-700">not in master</span>}
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-2 py-1.5"><Input value={ln.roll_no} onChange={(e) => setLine(i, "roll_no", e.target.value)} className="rounded-sm h-8 text-sm font-mono-num" data-testid={`grn-roll-${i}`} /></td>
                    <td className="px-2 py-1.5"><Input value={ln.master_lot} onChange={(e) => setLine(i, "master_lot", e.target.value)} className="rounded-sm h-8 text-sm font-mono-num" placeholder="same as roll" /></td>
                    <td className="px-2 py-1.5">
                      <Input type="number" min="0" step="any" value={ln.received_qty} onChange={(e) => setLine(i, "received_qty", e.target.value)}
                        className="rounded-sm h-8 text-sm text-right w-28 ml-auto" placeholder={it?.uom === "KG" ? "kg" : "SQM"} data-testid={`grn-qty-${i}`} />
                      {/* The conversion, right where the SQM is typed */}
                      {it?.uom === "SQMTRS" && m != null && (
                        <div className="text-[11px] text-right font-mono-num font-semibold text-emerald-700 mt-0.5" data-testid={`grn-converted-${i}`}>
                          = {qty(m)} m
                        </div>
                      )}
                      {!it && Number(ln.received_qty) > 0 && (
                        <div className="text-[11px] text-right text-amber-700 mt-0.5">enter the item code to convert to metres</div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono-num pt-2" data-testid={`grn-metres-${i}`}>
                      <div className="text-sm font-semibold">{m ? `${qty(m)} ${unit(it.uom)}` : "—"}</div>
                      {it?.uom === "SQMTRS" && (
                        <div className="text-[11px] text-neutral-500">
                          {m ? `${qty(Number(ln.received_qty))} SQM ÷ ${it.roll_width / 1000} m` : `width ${it.roll_width} mm`}
                        </div>
                      )}
                      {it?.uom === "KG" && m != null && <div className="text-[11px] text-neutral-500">stocked in kg</div>}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono-num pt-2" data-testid={`grn-kg-${i}`}>
                      {(() => {
                        const w = weight(ln);
                        if (!w) return <div className="text-sm">—</div>;
                        if (w.kg == null) return <div className="text-[11px] text-amber-700 font-sans">enter GSM to convert</div>;
                        return (
                          <>
                            <div className="text-sm font-semibold">{qty(w.kg)} kg</div>
                            <div className="text-[11px] text-neutral-500" data-testid={`grn-kg-working-${i}`}>
                              {it.uom === "KG"
                                ? (w.lengthM != null ? `≈ ${qty(w.lengthM, 0)} m at ${w.gsm} GSM × ${qty(w.widthM, 3)} m` : "as received")
                                : `${w.gsm} GSM × ${qty(w.widthM, 3)} m × ${qty(w.lengthM, 0)} m ÷ 1000`}
                            </div>
                          </>
                        );
                      })()}
                    </td>
                    <td className="px-2 py-1.5">
                      <Input type="number" min="0" step="any" value={ln.rate} onChange={(e) => setLine(i, "rate", e.target.value)}
                        className="rounded-sm h-8 text-sm text-right w-28 ml-auto" placeholder={it?.rate ? String(it.rate) : "₹"} data-testid={`grn-rate-${i}`} />
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono-num pt-2" data-testid={`grn-amount-${i}`}>
                      <div className="text-sm font-semibold">{amount != null ? money(Math.round(amount * 100) / 100) : "—"}</div>
                      {amount != null && ln.rate === "" && <div className="text-[11px] text-neutral-500">at item rate</div>}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap pt-2.5">
                      <button title="Copy line (next roll of the same item)" className="text-neutral-400 hover:text-[#FF5A00] mr-2"
                        onClick={() => setLines((ls) => [...ls.slice(0, i + 1), { ...ln, roll_no: "" }, ...ls.slice(i + 1)])}><Copy size={14} /></button>
                      <button title="Remove line" className="text-neutral-400 hover:text-rose-600" disabled={lines.length === 1}
                        onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {filled.length > 0 && (
              <tfoot className="border-t-2 border-neutral-200 bg-neutral-50" data-testid="grn-totals">
                <tr className="font-mono-num">
                  <td className="px-2 py-2 label-caps text-[10px]" colSpan={5}>Total · {filled.length} roll(s)</td>
                  <td className="px-2 py-2 text-right text-sm" data-testid="grn-total-qty">
                    {totals.sqm > 0 && <div>{qty(totals.sqm)} SQM</div>}
                    {totals.kg > 0 && <div>{qty(totals.kg)} kg</div>}
                  </td>
                  <td className="px-2 py-2 text-right" data-testid="grn-total-metres">
                    {totals.m > 0 && <div className="text-sm font-bold text-emerald-700">{qty(totals.m)} m</div>}
                    {totals.m > 0 && <div className="text-[11px] text-neutral-500 font-sans">converted from {qty(totals.sqm)} SQM</div>}
                  </td>
                  <td className="px-2 py-2 text-right" data-testid="grn-total-kg">
                    {totals.weight > 0 && <div className="text-sm font-bold">{qty(totals.weight)} kg</div>}
                    {totals.noGsm > 0 && <div className="text-[11px] text-amber-700 font-sans">{totals.noGsm} line(s) need GSM</div>}
                  </td>
                  <td></td>
                  <td className="px-2 py-2 text-right" data-testid="grn-total-amount">
                    <div className="text-sm font-bold">{money(Math.round(totals.amount * 100) / 100)}</div>
                    {totals.unpriced > 0 && <div className="text-[11px] text-amber-700 font-sans">{totals.unpriced} line(s) without a rate</div>}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="flex items-center gap-5">
          <button className="text-xs text-[#FF5A00] hover:underline inline-flex items-center gap-1 w-fit" onClick={() => setLines((ls) => [...ls, blankLine()])}>
            <Plus size={12} /> Add line
          </button>
          <button className="text-xs text-[#FF5A00] hover:underline inline-flex items-center gap-1 w-fit" onClick={() => startNewItem(null)}
            data-testid="grn-new-item">
            <Plus size={12} /> New item (not in the item list)
          </button>
        </div>
        {newItem && (
          <ItemDialog preset={newItem.preset} onClose={() => setNewItem(null)} onSaved={itemCreated} />
        )}
        <DialogFooter>
          <span className="mr-auto text-xs text-neutral-500 self-center">{filled.length} roll(s)</span>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="grn-save" className={SAVE_BTN} onClick={save} disabled={busy || !head.supplier.trim() || !filled.length || invalid}>
            {busy ? "Saving…" : "Save GRN"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------------
// Incoming QC on received rolls
// ----------------------------------------------------------------------------
export function QcDialog({ rollNos, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ qc_ar_no: "", status: "APPROVED", date: today() });
  const save = () => submit(setBusy, () => api.post("/stores/qc", { roll_nos: rollNos, ...f }),
    ({ data }) => { toast.success(`QC recorded on ${data.updated} roll(s)`); onDone(); });
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Incoming QC</DialogTitle>
          <DialogDescription>{rollNos.length} roll(s): {rollNos.slice(0, 4).join(", ")}{rollNos.length > 4 ? "…" : ""}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="QC AR number *" value={f.qc_ar_no} onChange={(v) => setF({ ...f, qc_ar_no: v })} placeholder="0743/26-27" testid="qc-ar" />
          <Field label="Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} />
          <Labeled label="Result" className="col-span-2">
            <SmallSelect value={f.status} onChange={(v) => setF({ ...f, status: v })} testid="qc-status"
              options={[{ value: "APPROVED", label: "Approved — can be issued" }, { value: "NOT APPROVED", label: "Rejected — blocked from issue" }]} />
          </Labeled>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="qc-save" className={SAVE_BTN} onClick={save} disabled={busy || !f.qc_ar_no.trim()}>{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------------
// Issue to a BPR: start from the job, pick rolls (oldest first), alternate widths
// ----------------------------------------------------------------------------
// A plan figure (width or ups) editable right in the Issue dialog — covers the case where there's no
// alternate stock to pick a "Replan" chip from at all, so Stores still has a way to correct the plan
// before issuing against whatever width is actually on hand.
function PlanTile({ label, value, suffix = "", onSave, testId }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value ?? "");
  const start = () => { setV(value ?? ""); setEditing(true); };
  const save = async () => {
    if (v === "" || Number(v) <= 0) { toast.error(`Enter a valid ${label.toLowerCase()}`); return; }
    await onSave(Number(v));
    setEditing(false);
  };
  if (editing) {
    return (
      <div>
        <div className="label-caps text-[10px]">{label}</div>
        <div className="flex items-center gap-1">
          <Input type="number" autoFocus value={v} onChange={(e) => setV(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && save()} className="rounded-sm h-7 w-20 font-mono-num text-xs" data-testid={`${testId}-input`} />
          <button type="button" onClick={save} className="text-[#FF5A00] text-xs hover:underline" data-testid={`${testId}-save`}>Save</button>
          <button type="button" onClick={() => setEditing(false)} className="text-neutral-400 text-xs hover:underline">✕</button>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="label-caps text-[10px]">{label}</div>
      <button type="button" onClick={start} className="font-mono-num hover:text-[#FF5A00] hover:underline" data-testid={testId}>
        {value ? `${value}${suffix}` : "—"} <span className="text-[10px] text-neutral-400">(edit)</span>
      </button>
    </div>
  );
}

export function IssueDialog({ initialBpr = "", onClose, onDone }) {
  const { user } = useAuth();
  const lists = useStoreLists();
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState(today());
  const [department, setDepartment] = useState("PRINTING");
  const [bpr, setBpr] = useState(initialBpr);
  const [ctx, setCtx] = useState(null);
  const [shownItem, setShownItem] = useState(null);
  const [suggested, setSuggested] = useState([]);
  const [lines, setLines] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [extra, setExtra] = useState(null);
  // The BPR's stores sheet (paper form PF005/F02-00): what stores writes here is saved to the job's Stores Issue stage.
  const [draft, setDraft] = useState({});
  const [sheetOpen, setSheetOpen] = useState(true);
  const stageDone = !!ctx?.stage?.completed_at;
  // A completed stage is locked, except for the admin, who can always correct a BPR's sheet.
  const locked = stageDone && !isAdmin(user);
  const dirty = Object.keys(draft).length > 0 && !locked;

  const loadBpr = (value = bpr) => {
    const b = value.trim().toUpperCase();
    if (!b) return;
    api.get(`/stores/bpr/${encodeURIComponent(b)}`)
      .then(({ data }) => { setCtx(data); setBpr(b); setDraft({}); setShownItem(data.planned_item); setSuggested(data.rolls); })
      .catch((e) => { setCtx(null); setSuggested([]); toast.error(formatApiError(e)); });
  };

  // When the planned width isn't in stock but a narrower alternate is, re-plan to what it actually
  // fits — a real PATCH to the job itself (stock_width_mm + ups_across), the same fields every other
  // screen (Order Detail, Master Job Card, Production Flow) reads, so the new plan is what everyone
  // sees from here on, not a Stores-only note.
  const replan = async (width, ups) => {
    if (!ctx) return;
    if (!window.confirm(`Replan ${ctx.job.mpwo_number || bpr} to ${ups} ups across at ${width} mm? This updates the job's own plan for every screen, not just this issue.`)) return;
    await patchPlan({ stock_width_mm: width, ups_across: ups }, `Replanned to ${ups} ups at ${width} mm`);
  };
  // One field at a time (used when there's no in-stock alternate to pick a width from at all, so
  // Stores/Planning corrects Width or Ups by hand instead).
  const patchPlan = async (fields, okMsg) => {
    if (!ctx) return;
    try {
      await api.patch(`/jobs/${ctx.job.id}`, fields);
      toast.success(okMsg);
      loadBpr(bpr);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  // The same finish as the job's Stage form: save the sheet, complete the Stores stage, job moves on.
  const finish = (withIssue) => {
    if (!window.confirm(`Complete the Stores stage of ${bpr}? The sheet is locked, the job moves to In Production and Printing unlocks.`)) return;
    submit(setBusy, async () => {
      if (dirty) await postStage();
      if (withIssue) await postIssue();
      return api.post(`/jobs/${ctx.job.id}/stages/stores_issue`, { data: {}, complete: true });
    }, () => { toast.success(`${bpr}: Stores stage completed, job moved to In Production`); onDone(); });
  };
  const postStage = () => api.post(`/jobs/${ctx.job.id}/stages/stores_issue`, { data: draft, complete: false });
  const saveStage = () => submit(setBusy, postStage, () => {
    toast.success("Saved to the job's Production Flow");
    setDraft({});
    api.get(`/stores/bpr/${encodeURIComponent(bpr)}`).then(({ data }) => setCtx(data));
  });
  useEffect(() => { if (initialBpr) loadBpr(initialBpr); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showItem = (code) => {
    setShownItem(code);
    // only rolls QC has released can be issued; ones still with QC do not show here
    api.get("/stores/rolls", { params: { item_code: code, available: true, issuable: true, limit: 25 } })
      .then(({ data }) => setSuggested(data.items));
  };
  const addRoll = (r) => {
    if (!r || lines.some((l) => l.roll.roll_no === r.roll_no)) return;
    setLines((ls) => [...ls, { roll: r, qty: String(Math.round(r.balance * 1000) / 1000) }]);
  };
  const departments = useMemo(() => [...new Set(["PRINTING", ...lists.departments])], [lists.departments]);
  const needsBpr = department === "PRINTING";
  const issuing = lines.reduce((s, l) => s + (Number(l.qty) || 0), 0);
  const invalid = lines.some((l) => !(Number(l.qty) > 0) || Number(l.qty) > l.roll.balance + 1e-6);
  // The width that matters: once a roll is actually added, THAT width — browsing a different width group
  // (shownItem) before adding anything is just a preview of what it would take.
  const altWidth = lines.length ? lines[0].roll.width : (ctx && ctx.planned_item && shownItem !== ctx.planned_item ? ctx.alternates.find((a) => a.item_code === shownItem)?.width : null);
  const adj = ctx && altWidth ? adjustedRequired(ctx.job, ctx.required, altWidth) : null;

  const postIssue = () => api.post("/stores/issues", {
    date, department, bpr_no: bpr.trim() || null, remarks: remarks || null,
    lines: lines.map((l) => ({ roll_no: l.roll.roll_no, qty: Number(l.qty) })),
  });
  // Whatever was written on the sheet is saved with the issue, so nothing typed is lost.
  const save = () => {
    if (!window.confirm(`Issue ${lines.length} roll(s), ${issuing.toLocaleString("en-IN")} ${lines[0] ? unit(lines[0].roll.uom) : ""}${bpr ? ` to ${bpr}` : ""}? This deducts stock right away.`)) return;
    submit(setBusy, async () => {
      if (dirty) await postStage();
      return postIssue();
    }, ({ data }) => { toast.success(`${data.doc_no} · ${data.lines} roll(s) issued${bpr ? ` to ${bpr}` : ""}`); onDone(); });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Issue material</DialogTitle>
          <DialogDescription>Enter the BPR to see the job's material and the rolls in stock, oldest first.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <Field label="Date" type="date" value={date} onChange={setDate} />
          <Labeled label="Issue to">
            <SmallSelect value={department} onChange={setDepartment} testid="issue-dept"
              options={departments.map((d) => ({ value: d, label: d.charAt(0) + d.slice(1).toLowerCase() }))} />
          </Labeled>
          <Labeled label={needsBpr ? "BPR number *" : "BPR number"}>
            <div className="flex gap-2">
              <Input data-testid="issue-bpr" value={bpr} onChange={(e) => setBpr(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadBpr()}
                placeholder="MPWO-…" className="rounded-sm h-8 text-sm font-mono-num" />
              <Button variant="outline" size="sm" className="rounded-sm h-8" onClick={() => loadBpr()}>Load</Button>
            </div>
          </Labeled>
          <Field label="Remarks" value={remarks} onChange={setRemarks} />
        </div>

        {ctx && (
          <div className="border border-neutral-200 bg-neutral-50 p-3 grid grid-cols-2 md:grid-cols-8 gap-3 text-sm" data-testid="issue-job">
            <div className="md:col-span-2">
              <div className="label-caps text-[10px]">Job</div>
              <div className="font-medium truncate">{ctx.job.customer_name}</div>
              <div className="text-xs text-neutral-500 truncate">{ctx.job.product_name}</div>
            </div>
            <div><div className="label-caps text-[10px]">Material</div><div className="font-mono-num">{ctx.job.substrate_code || "—"}</div></div>
            <PlanTile label="Planned width" value={ctx.job.stock_width_mm} suffix=" mm" testId="issue-planned-width"
              onSave={(w) => patchPlan({ stock_width_mm: w }, `Planned width set to ${w} mm`)} />
            <PlanTile label="Planned ups across" value={ctx.job.ups_across} testId="issue-planned-ups"
              onSave={(ups) => {
                // Same formula Planning uses: (label width + gap across) x ups + a fixed trim allowance —
                // so changing Ups here keeps Width consistent with it, not left stale at the old plan.
                const lw = Number(ctx.job.label_width_mm) || 0;
                const gap = Number(ctx.job.gap_across_mm) || 0;
                const width = lw > 0 ? Math.round((lw + gap) * ups + 12) : null;
                const fields = width ? { ups_across: ups, stock_width_mm: width } : { ups_across: ups };
                return patchPlan(fields, width ? `Replanned to ${ups} ups · width recalculated to ${width} mm` : `Ups across set to ${ups}`);
              }} />
            <PlanTile label="Planned ups down" value={ctx.job.ups_along} testId="issue-planned-ups-along"
              onSave={(ups) => patchPlan({ ups_along: ups }, `Ups down set to ${ups}`)} />
            <div>
              <div className="label-caps text-[10px]">Required{altWidth ? ` · at ${altWidth} mm` : ""}</div>
              {altWidth && adj ? (
                <div data-testid="issue-adjusted-required">
                  <div className={`font-mono-num ${adj.metres == null ? "text-rose-700" : adj.fitUps < Number(ctx.job.ups_across) ? "text-amber-700" : ""}`}>
                    {adj.metres == null ? "Too narrow" : `${qty(adj.metres, 0)} m`}
                  </div>
                  <div className="text-[11px] text-neutral-500">{adj.fitUps} of {ctx.job.ups_across} ups fit</div>
                </div>
              ) : (
                <div className="font-mono-num">{ctx.required ? `${qty(ctx.required, 0)} m` : "—"}</div>
              )}
            </div>
            <div>
              <div className="label-caps text-[10px]">Issued so far</div>
              <div className="font-mono-num">{qty(Math.max(ctx.net_issued, 0), 0)} m</div>
              {ctx.returned > 0 && (
                <div className="text-[11px] text-neutral-500 font-mono-num">{qty(ctx.issued, 0)} out · {qty(ctx.returned, 0)} back</div>
              )}
            </div>
          </div>
        )}

        {/* What's available to issue, shown right under the job summary — not buried below the
            printed BPR sheet, since deciding what to issue comes before filling in the paperwork. */}
        {ctx && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
            <div className="lg:col-span-3 border border-neutral-200">
              <div className="px-3 py-2 border-b border-neutral-200 flex items-center justify-between gap-2">
                <div className="label-caps text-[10px]">In stock{shownItem ? ` · ${shownItem}` : ""}</div>
                {ctx.planned_item && shownItem !== ctx.planned_item && (
                  <button className="text-xs text-[#FF5A00] hover:underline" onClick={() => { setShownItem(ctx.planned_item); setSuggested(ctx.rolls); }}>
                    Back to planned width
                  </button>
                )}
              </div>
              <div className="max-h-64 overflow-y-auto divide-y divide-neutral-100">
                {suggested.length === 0 && (
                  <div className="px-3 py-6 text-center text-sm text-neutral-400">
                    {ctx.planned_item ? "No QC-cleared rolls of the planned width." : "This job has no material/width on its job card."} Pick an alternate width or search a roll.
                  </div>
                )}
                {suggested.map((r) => {
                  const added = lines.some((l) => l.roll.roll_no === r.roll_no);
                  return (
                    <div key={r.roll_no} className="px-3 py-2 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-mono-num text-xs font-semibold">{r.roll_no}</div>
                        <div className="text-[11px] text-neutral-500">{r.received_date} · lot {r.master_lot} · <Pill value={r.qc_status} /></div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono-num text-sm">{qty(r.balance, 0)} {unit(r.uom)}</span>
                        <Button size="sm" variant="outline" className="rounded-sm h-7" disabled={added} onClick={() => addRoll(r)}>{added ? "Added" : "Add"}</Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="lg:col-span-2 border border-neutral-200">
              <div className="px-3 py-2 border-b border-neutral-200 label-caps text-[10px]">Other widths of {ctx.job.substrate_code || "this material"}</div>
              <div className="max-h-64 overflow-y-auto divide-y divide-neutral-100">
                {ctx.alternates.length === 0 && <div className="px-3 py-6 text-center text-sm text-neutral-400">None in stock.</div>}
                {ctx.alternates.map((a) => {
                  const aAdj = adjustedRequired(ctx.job, ctx.required, a.width);
                  const short = aAdj && aAdj.fitUps > 0 && aAdj.fitUps < Number(ctx.job.ups_across);
                  return (
                    <div key={a.item_code} role="button" tabIndex={0} onClick={() => showItem(a.item_code)}
                      onKeyDown={(e) => e.key === "Enter" && showItem(a.item_code)}
                      className={`w-full text-left px-3 py-2 flex justify-between hover:bg-orange-50 cursor-pointer ${shownItem === a.item_code ? "bg-orange-50" : ""}`}>
                      <span className="font-mono-num text-xs">{a.item_code}</span>
                      <span className="text-right">
                        <span className="font-mono-num text-xs text-neutral-600 block">{qty(a.balance, 0)} {unit(a.uom)} · {a.rolls} roll(s)</span>
                        {aAdj && (
                          <span className={`font-mono-num text-[11px] block ${aAdj.metres == null ? "text-rose-600" : short ? "text-amber-700" : "text-neutral-400"}`}>
                            {aAdj.metres == null ? "too narrow to use" : `needs ${qty(aAdj.metres, 0)} m`}
                          </span>
                        )}
                        {aAdj && aAdj.fitUps > 0 && (
                          <span className="text-[10px] text-neutral-400 block">
                            {aAdj.fitUps} of {ctx.job.ups_across} planned ups fit at this width
                          </span>
                        )}
                        {short && (
                          <button type="button" data-testid={`replan-${a.item_code}`}
                            onClick={(e) => { e.stopPropagation(); replan(a.width, aAdj.fitUps); }}
                            className="text-[10px] text-[#FF5A00] hover:underline mt-0.5">
                            Replan job to {aAdj.fitUps} ups →
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {ctx && (
          <div className="border border-neutral-200" data-testid="issue-sheet">
            <div className="px-3 py-2 flex flex-wrap items-center justify-between gap-2 bg-neutral-50">
              <div className="label-caps text-[10px]">BPR sheet · Stores — Issuing of Material</div>
              <div className="flex items-center gap-2">
                {sheetOpen && (
                  <Button size="sm" variant="outline" className="rounded-sm h-7 gap-1" data-testid="issue-print"
                    onClick={() => printElement(document.querySelector('[data-testid="issue-sheet"] [data-testid="stores-sheet"]'))}>
                    <Printer size={12} /> Print
                  </Button>
                )}
                {sheetOpen && !locked && (
                  <Button size="sm" variant="outline" className="rounded-sm h-7" disabled={!dirty || busy} onClick={saveStage} data-testid="issue-save-stage">
                    Save stage
                  </Button>
                )}
                {sheetOpen && !stageDone && (
                  <Button size="sm" className="rounded-sm h-7 bg-[#FF5A00] hover:bg-[#e04f00] text-white" data-testid="issue-complete-stage"
                    disabled={busy || lines.length > 0 || !(ctx.net_issued > 0)} onClick={() => finish(false)}
                    title={lines.length ? "Rolls are waiting to be issued: use Issue & complete stage" : !(ctx.net_issued > 0) ? "Nothing has been issued to this BPR yet" : undefined}>
                    Save & complete stage
                  </Button>
                )}
                <button type="button" className="text-xs text-[#FF5A00] hover:underline" onClick={() => setSheetOpen((o) => !o)}>
                  {sheetOpen ? "Hide" : "Show"}
                </button>
              </div>
            </div>
            {sheetOpen && (
              <div className="p-3 space-y-2">
                <SheetBody ctx={ctx} data={{ ...(ctx.stage?.data || {}), ...draft }} set={(k, v) => setDraft((d) => ({ ...d, [k]: v }))}
                  readOnly={locked}
                  pending={lines.map((l) => ({
                    key: l.roll.roll_no, issued: Number(l.qty) || 0, qc: l.roll.qc_ar_no || "", width: l.roll.width,
                    item_name: l.roll.item_name, grn: l.roll.grn_no, lot: l.roll.master_lot, supplier: l.roll.supplier,
                  }))} />
                <div className="text-[11px] text-neutral-500">
                  {stageDone ? (locked ? "This job's Stores stage is completed; the sheet is locked." : "This job's Stores stage is completed; as admin you can still edit the sheet and press Save stage.")
                    : "Write remarks, inks or any detail here and press Save stage: it appears in this job's Production Flow (Stores — RM Issue). Rolls you add below show as 'to be issued'."}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="border border-neutral-200">
          <div className="px-3 py-2 border-b border-neutral-200 flex items-center justify-between gap-3">
            <div className="label-caps text-[10px]">Issuing</div>
            <div className="w-72"><RollPicker issuable value={extra?.roll_no} onPick={(r) => { setExtra(r); addRoll(r); }} placeholder="Add any QC-approved roll" testid="issue-roll-search" /></div>
          </div>
          <table className="w-full text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap">
            <tbody>
              {lines.length === 0 && <tr><td className="px-3 py-5 text-center text-sm text-neutral-400">No rolls added yet.</td></tr>}
              {lines.map((l, i) => (
                <tr key={l.roll.roll_no} className="border-t border-neutral-100 first:border-0">
                  <td className="px-3 py-2"><span className="font-mono-num text-xs font-semibold">{l.roll.roll_no}</span><span className="text-xs text-neutral-500"> · {l.roll.item_code}</span></td>
                  <td className="px-3 py-2 text-xs text-neutral-500 text-right">balance {qty(l.roll.balance)} {unit(l.roll.uom)}</td>
                  <td className="px-3 py-2 w-36">
                    <Input type="number" value={l.qty} onChange={(e) => setLines((ls) => ls.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))}
                      className={`rounded-sm h-8 text-sm text-right ${Number(l.qty) > l.roll.balance + 1e-6 ? "border-rose-400" : ""}`} data-testid={`issue-qty-${i}`} />
                  </td>
                  <td className="px-3 py-2 w-8">
                    <button title="Remove" className="text-neutral-400 hover:text-rose-600" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DialogFooter>
          <span className="mr-auto text-xs text-neutral-500 self-center font-mono-num">
            Issuing {qty(issuing, 0)}{ctx?.required ? ` of ${qty(ctx.required, 0)} m required` : ""}
          </span>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="issue-save" className={SAVE_BTN} onClick={save}
            disabled={busy || !lines.length || invalid || (needsBpr && !bpr.trim())}>
            {busy ? "Issuing…" : "Issue"}
          </Button>
          {ctx && !stageDone && (
            <Button data-testid="issue-complete" className={SAVE_BTN} onClick={() => finish(true)}
              disabled={busy || !lines.length || invalid || !bpr.trim()}>
              Issue & complete stage
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------------
// Production return
// ----------------------------------------------------------------------------
export function ReturnDialog({ initialBpr = "", onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [roll, setRoll] = useState(null);
  const [f, setF] = useState({ date: today(), bpr_no: initialBpr, department: "PRINTING", qty: "", reason: "" });
  // Opened from a job, the return belongs to that job; otherwise default to the roll's last BPR.
  const pick = (r) => { setRoll(r); if (r?.last_bpr_no && !initialBpr) setF((p) => ({ ...p, bpr_no: r.last_bpr_no })); };
  const save = () => submit(setBusy, () => api.post("/stores/returns", {
    date: f.date, roll_no: roll.roll_no, qty: Number(f.qty), department: f.department,
    bpr_no: f.bpr_no.trim() || null, reason: f.reason || null,
  }), ({ data }) => { toast.success(`${data.doc_no} · ${qty(f.qty)} returned to ${roll.roll_no}`); onDone(); });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Production return</DialogTitle>
          <DialogDescription>Unused material back to stores, against the roll it came from.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Labeled label="Roll *" className="col-span-2"><RollPicker available={false} value={roll?.roll_no} onPick={pick} testid="return-roll" /></Labeled>
          {roll && (
            <div className="col-span-2 text-xs text-neutral-600 border border-neutral-200 bg-neutral-50 px-3 py-2">
              {roll.item_code} · {roll.item_name} · issued {qty(roll.issued_qty)} / returned {qty(roll.returned_qty)} {unit(roll.uom)}
            </div>
          )}
          <Field label="Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} />
          <Field label="Qty returned (m / kg) *" type="number" value={f.qty} onChange={(v) => setF({ ...f, qty: v })} testid="return-qty" />
          <Field label="BPR number" value={f.bpr_no} onChange={(v) => setF({ ...f, bpr_no: v })} placeholder="MPWO-…" />
          <Field label="From department" value={f.department} onChange={(v) => setF({ ...f, department: v.toUpperCase() })} />
          <Field label="Reason" value={f.reason} onChange={(v) => setF({ ...f, reason: v })} className="col-span-2" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="return-save" className={SAVE_BTN} onClick={save} disabled={busy || !roll || !(Number(f.qty) > 0)}>{busy ? "Saving…" : "Save return"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------------------------------------------------------------
// Roll slitting in stores: one wide roll into narrower child rolls
// ----------------------------------------------------------------------------
export function SlittingDialog({ onClose, onDone }) {
  const lists = useStoreLists();
  const [busy, setBusy] = useState(false);
  const [parent, setParent] = useState(null);
  const [f, setF] = useState({ date: today(), length: "", operator: "", remarks: "" });
  const [children, setChildren] = useState([{ item_code: "", length: "" }, { item_code: "", length: "" }]);

  const parentItem = lists.items.find((i) => i.sku === parent?.item_code);
  const childOptions = lists.items.filter(
    (i) => parentItem && i.paper_code === parentItem.paper_code && i.roll_width < parentItem.roll_width);
  const widthOf = (code) => lists.items.find((i) => i.sku === code)?.roll_width || 0;
  const usedWidth = children.reduce((s, c) => s + widthOf(c.item_code), 0);

  const pick = (r) => {
    setParent(r);
    if (r) {
      setF((p) => ({ ...p, length: String(r.balance) }));
      setChildren((cs) => cs.map((c) => ({ ...c, length: String(r.balance) })));
    }
  };
  const setChild = (i, k, v) => setChildren((cs) => cs.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const valid = parent && Number(f.length) > 0 && Number(f.length) <= parent.balance + 1e-6
    && children.every((c) => childOptions.some((o) => o.sku === c.item_code) && Number(c.length) > 0)
    && usedWidth <= (parentItem?.roll_width || 0);

  const save = () => submit(setBusy, () => api.post("/stores/slitting", {
    date: f.date, parent_roll: parent.roll_no, length: Number(f.length), operator: f.operator || null,
    remarks: f.remarks || null, children: children.map((c) => ({ item_code: c.item_code, length: Number(c.length) })),
  }), ({ data }) => { toast.success(`${data.doc_no} · new rolls ${data.child_rolls.join(", ")}`); onDone(); });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Slit a roll</DialogTitle>
          <DialogDescription>Cut a wider roll into narrower rolls of the same material. Child rolls are numbered after the parent.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Labeled label="Parent roll *" className="col-span-2"><RollPicker issuable value={parent?.roll_no} onPick={pick} testid="slit-parent" /></Labeled>
          {parent && (
            <div className="col-span-2 text-xs text-neutral-600 border border-neutral-200 bg-neutral-50 px-3 py-2">
              {parent.item_code} · {parent.item_name} · {parent.width} mm · balance {qty(parent.balance)} {unit(parent.uom)}
            </div>
          )}
          <Field label="Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} />
          <Field label="Length used from parent *" type="number" value={f.length} onChange={(v) => setF({ ...f, length: v })} />
          <Field label="Operator" value={f.operator} onChange={(v) => setF({ ...f, operator: v })} />
          <Field label="Remarks" value={f.remarks} onChange={(v) => setF({ ...f, remarks: v })} />
        </div>
        <div className="border border-neutral-200">
          <div className="px-3 py-2 border-b border-neutral-200 flex justify-between label-caps text-[10px]">
            <span>Child rolls</span>
            <span className={usedWidth > (parentItem?.roll_width || 0) ? "text-rose-700" : ""}>
              {usedWidth} / {parentItem?.roll_width || "—"} mm used
            </span>
          </div>
          {children.map((c, i) => (
            <div key={i} className="px-3 py-2 grid grid-cols-12 gap-2 items-center border-t border-neutral-100 first:border-0">
              <span className="col-span-2 font-mono-num text-xs text-neutral-500">{parent ? `${parent.roll_no}/${String(i + 1).padStart(2, "0")}` : `#${i + 1}`}</span>
              <div className="col-span-6">
                <SmallSelect value={c.item_code} onChange={(v) => setChild(i, "item_code", v)}
                  options={childOptions.length ? childOptions.map((o) => ({ value: o.sku, label: `${o.sku} · ${o.roll_width} mm` })) : [{ value: "none", label: parent ? "No narrower items" : "Pick the parent first" }]} />
              </div>
              <Input type="number" value={c.length} onChange={(e) => setChild(i, "length", e.target.value)} className="col-span-3 rounded-sm h-8 text-sm text-right" placeholder="length" />
              <button className="col-span-1 text-neutral-400 hover:text-rose-600" disabled={children.length === 1}
                onClick={() => setChildren((cs) => cs.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
            </div>
          ))}
          <button className="px-3 py-2 border-t border-neutral-100 w-full text-left text-xs text-[#FF5A00] hover:underline inline-flex items-center gap-1"
            onClick={() => setChildren((cs) => [...cs, { item_code: "", length: f.length }])}><Plus size={12} /> Add child roll</button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="slit-save" className={SAVE_BTN} onClick={save} disabled={busy || !valid}>{busy ? "Saving…" : "Save slitting"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
